import React, { Component } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  Collapse,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  BugReport as BugIcon,
} from '@mui/icons-material';

/**
 * Global Error Boundary Component
 * Catches JavaScript errors anywhere in the component tree and displays a fallback UI
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console in development
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // In production, you could send this to an error reporting service
    // Example: sendErrorToService(error, errorInfo);
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error, errorInfo) => {
    // Log error to backend (optional - implement if needed)
    const errorLog = {
      message: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Store in localStorage for debugging
    try {
      const existingLogs = JSON.parse(localStorage.getItem('errorLogs') || '[]');
      existingLogs.push(errorLog);
      // Keep only last 10 errors
      if (existingLogs.length > 10) existingLogs.shift();
      localStorage.setItem('errorLogs', JSON.stringify(existingLogs));
    } catch (e) {
      console.warn('Failed to save error log:', e);
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleToggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  handleReportError = () => {
    const { error, errorInfo } = this.state;
    const subject = encodeURIComponent('Bug Report: Application Error');
    const body = encodeURIComponent(`
Error Message: ${error?.message}

Stack Trace:
${error?.stack}

Component Stack:
${errorInfo?.componentStack}

URL: ${window.location.href}
Time: ${new Date().toISOString()}
    `);
    
    // Open email client with pre-filled bug report
    // Replace with your support email
    window.open(`mailto:support@carrierfinderpro.com?subject=${subject}&body=${body}`);
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails } = this.state;
      
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f5f5f5',
            p: 2,
          }}
        >
          <Container maxWidth="md">
            <Paper
              elevation={3}
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 3,
              }}
            >
              {/* Error Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'error.lighter',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
              </Box>

              {/* Error Message */}
              <Typography variant="h4" gutterBottom fontWeight="bold" color="text.primary">
                Oops! Something went wrong
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                We're sorry, but something unexpected happened. Our team has been notified 
                and we're working to fix it.
              </Typography>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleReload}
                  size="large"
                >
                  Reload Page
                </Button>
                
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<HomeIcon />}
                  onClick={this.handleGoHome}
                  size="large"
                >
                  Go to Home
                </Button>
                
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<BugIcon />}
                  onClick={this.handleReportError}
                  size="large"
                >
                  Report Bug
                </Button>
              </Box>

              {/* Error Details (Collapsible) */}
              {process.env.NODE_ENV === 'development' && (
                <>
                  <Button
                    size="small"
                    onClick={this.handleToggleDetails}
                    sx={{ mb: 2 }}
                  >
                    {showDetails ? 'Hide' : 'Show'} Technical Details
                  </Button>
                  
                  <Collapse in={showDetails}>
                    <Alert severity="error" sx={{ textAlign: 'left', mt: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Error Message:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace' }}>
                        {error?.message}
                      </Typography>
                      
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Stack Trace:
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          fontSize: '0.75rem',
                          overflow: 'auto',
                          maxHeight: 200,
                          bgcolor: 'grey.100',
                          p: 1,
                          borderRadius: 1,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {error?.stack}
                      </Box>
                      
                      {errorInfo?.componentStack && (
                        <>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                            Component Stack:
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              fontSize: '0.75rem',
                              overflow: 'auto',
                              maxHeight: 200,
                              bgcolor: 'grey.100',
                              p: 1,
                              borderRadius: 1,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {errorInfo.componentStack}
                          </Box>
                        </>
                      )}
                    </Alert>
                  </Collapse>
                </>
              )}
            </Paper>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
